import { useEffect } from 'react';

const CF_ANALYTICS_TOKEN = import.meta.env.VITE_CF_ANALYTICS_TOKEN;
const HOTJAR_SITE_ID = 6649894;
const HOTJAR_SCRIPT_VERSION = 6;

type HotjarQueue = ((...args: unknown[]) => void) & { q?: unknown[][] };
type HotjarWindow = Window & {
  hj?: HotjarQueue;
  _hjSettings?: {
    hjid: number;
    hjsv: number;
  };
};

interface AnalyticsLoaderProps {
  enabled: boolean;
}

export function AnalyticsLoader({ enabled }: AnalyticsLoaderProps) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    if (CF_ANALYTICS_TOKEN) {
      const existingCloudflare = document.querySelector('script[data-cf-beacon]');
      if (!existingCloudflare) {
        const cloudflareScript = document.createElement('script');
        cloudflareScript.defer = true;
        cloudflareScript.src = 'https://static.cloudflareinsights.com/beacon.min.js';
        cloudflareScript.setAttribute('data-cf-beacon', JSON.stringify({ token: CF_ANALYTICS_TOKEN }));
        document.head.appendChild(cloudflareScript);
      }
    }

    const existingHotjar = document.querySelector(`script[data-hotjar-id="${HOTJAR_SITE_ID}"]`);
    if (existingHotjar) return;

    const hotjarWindow = window as HotjarWindow;
    if (!hotjarWindow.hj) {
      const queueFn = ((...args: unknown[]) => {
        queueFn.q = queueFn.q || [];
        queueFn.q.push(args);
      }) as HotjarQueue;
      hotjarWindow.hj = queueFn;
    }

    hotjarWindow._hjSettings = { hjid: HOTJAR_SITE_ID, hjsv: HOTJAR_SCRIPT_VERSION };

    const hotjarScript = document.createElement('script');
    hotjarScript.async = true;
    hotjarScript.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_SITE_ID}.js?sv=${HOTJAR_SCRIPT_VERSION}`;
    hotjarScript.setAttribute('data-hotjar-id', String(HOTJAR_SITE_ID));
    document.head.appendChild(hotjarScript);
  }, [enabled]);

  return null;
}
